// Letters that Unicode decomposition (NFKD) cannot split into base + accent.
const SPECIAL: Record<string, string> = {
  "ß": "ss", "ẞ": "SS", "æ": "ae", "Æ": "AE", "œ": "oe", "Œ": "OE",
  "ø": "o", "Ø": "O", "ł": "l", "Ł": "L", "đ": "d", "Đ": "D",
  "ð": "d", "Ð": "D", "þ": "th", "Þ": "TH", "ı": "i", "ħ": "h", "Ħ": "H",
};

export interface SlugOptions { maxLength?: number; stopWords?: boolean; andForAmp?: boolean; keepUnicode?: boolean; dedupe?: boolean }

const STOP = new Set("a an the of and or to in on at for with by is are".split(" "));

export function toSlug(input: string, separator = "-", lowercase = true, o: SlugOptions = {}): string {
  const sep = separator || "-";
  if (o.andForAmp) input = input.replace(/\s*&\s*/g, " and ");
  if (o.stopWords) {
    const words = input.split(/\s+/);
    const kept = words.filter((w) => !STOP.has(w.toLowerCase().replace(/[^a-z]/g, "")));
    if (kept.length) input = kept.join(" ");
  }
  const keep = o.keepUnicode ? "\\p{L}\\p{N}" : "a-zA-Z0-9";
  const s = input
    .replace(/[ßẞæÆœŒøØłŁđĐðÐþÞıħĦ]/g, c => SPECIAL[c] ?? c)
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/_/g, " ") // underscores separate words
    .replace(new RegExp(`[^${keep}\\s-]`, "gu"), "")
    .trim()
    .replace(/[\s-]+/g, sep);
  // Trim separators left at the ends.
  let start = 0;
  let end = s.length;
  while (start < end && s.startsWith(sep, start)) start += sep.length;
  while (end > start && s.endsWith(sep) && s.slice(0, end).endsWith(sep)) end -= sep.length;
  let out = s.slice(start, end);
  if (o.maxLength && out.length > o.maxLength) {
    const cut = out.slice(0, o.maxLength);
    const at = cut.lastIndexOf(sep);
    out = at > 0 ? cut.slice(0, at) : cut; // cut at a word boundary
  }
  return lowercase ? out.toLowerCase() : out;
}

/** One output line per input line, so slugs stay aligned with titles. */
export function toSlugs(input: string, separator = "-", lowercase = true, o: SlugOptions = {}): string {
  if (!input.includes("\n")) return toSlug(input, separator, lowercase, o);
  const lines = input.split(/\r?\n/).map(l => toSlug(l, separator, lowercase, o));
  if (!o.dedupe) return lines.join("\n");
  const seen = new Map<string, number>();
  return lines.map(l => {
    if (!l) return l;
    const n = (seen.get(l) ?? 0) + 1;
    seen.set(l, n);
    return n === 1 ? l : `${l}${separator || "-"}${n}`;
  }).join("\n");
}

/** True when a line has at least one letter or digit in any script (so a blank slug is due to non-Latin text, not to a line like "---"). */
export const hasWordChars = (line: string) => new RegExp("[\\p{L}\\p{N}]", "u").test(line);
